import { useState, useCallback, createContext, useMemo } from 'react';

import { DialogComponent } from './DialogComponent';
import { DialogOptions } from './types';

interface DialogContextValue {
    open: (id: string, options?: DialogOptions) => Promise<void>;
    update: (id: string, options?: DialogOptions) => void;
    clear: (id: string) => void;
    close: (id: string) => void;
}

export const DialogContext = createContext<DialogContextValue | null>(null);

const DEFAULT_OPTIONS: DialogOptions = {
    title: 'Are you sure?',
    description: '',
    content: null,
    confirmationText: 'Ok',
    cancellationText: 'Cancel',
    dialogProps: {},
    confirmationButtonProps: {},
    cancellationButtonProps: {},
    titleProps: {},
    contentProps: {},
    allowClose: true,
};

const buildOptions = (defaultOptions: DialogOptions = DEFAULT_OPTIONS, options?: DialogOptions): DialogOptions => {
    const dialogProps = {
        ...(defaultOptions.dialogProps || DEFAULT_OPTIONS.dialogProps),
        ...(options?.dialogProps || {}),
    };
    const confirmationButtonProps = {
        ...(defaultOptions.confirmationButtonProps || DEFAULT_OPTIONS.confirmationButtonProps),
        ...(options?.confirmationButtonProps || {}),
    };
    const cancellationButtonProps = {
        ...(defaultOptions.cancellationButtonProps || DEFAULT_OPTIONS.cancellationButtonProps),
        ...(options?.cancellationButtonProps || {}),
    };
    const titleProps = {
        ...(defaultOptions.titleProps || DEFAULT_OPTIONS.titleProps),
        ...(options?.titleProps || {}),
    };
    const contentProps = {
        ...(defaultOptions.contentProps || DEFAULT_OPTIONS.contentProps),
        ...(options?.contentProps || {}),
    };

    return {
        ...DEFAULT_OPTIONS,
        ...defaultOptions,
        ...options,
        dialogProps,
        confirmationButtonProps,
        cancellationButtonProps,
        titleProps,
        contentProps,
    };
};

interface DialogProviderProps {
    children: React.ReactNode;
    defaultOptions?: DialogOptions;
}

export const DialogProvider = ({ children, defaultOptions }: DialogProviderProps) => {
    const [state, setState] = useState<Record<string, DialogOptions>>({});

    const handleOpen = useCallback(
        (id: string, options?: DialogOptions) => {
            return new Promise<void>((resolve, reject) => {
                setState((state) => {
                    return {
                        ...state,
                        [id]: {
                            ...state[id],
                            ...buildOptions(defaultOptions, options),
                            open: true,
                            resolve,
                            reject,
                        },
                    };
                });
            });
        },
        [defaultOptions],
    );

    const handleUpdate = useCallback(
        (id: string, options?: DialogOptions) => {
            setState((state) => {
                return {
                    ...state,
                    [id]: {
                        ...buildOptions(defaultOptions, options),
                        open: state[id]?.open ?? false,
                        resolve: state[id]?.resolve,
                        reject: state[id]?.reject,
                    },
                };
            });
        },
        [defaultOptions],
    );

    const handleClear = useCallback((id: string) => {
        setState((state) => {
            delete state[id];
            return state;
        });
    }, []);

    const handleClose = useCallback((id: string) => {
        setState((state) => {
            return { ...state, [id]: { ...state[id], open: false } };
        });
    }, []);

    const value = useMemo(
        () => ({
            open: handleOpen,
            update: handleUpdate,
            clear: handleClear,
            close: handleClose,
        }),
        [handleOpen, handleUpdate, handleClear, handleClose],
    );

    return (
        <DialogContext.Provider value={value}>
            {children}
            {Object.keys(state).map((id) => (
                <DialogComponent
                    key={id}
                    open={Boolean(state[id]?.open)}
                    options={state[id] || DEFAULT_OPTIONS}
                    onClose={() => handleClose(id)}
                    onCancel={async () => {
                        state[id]?.reject?.();
                        handleClose(id);
                    }}
                    onConfirm={async () => {
                        state[id]?.resolve?.();
                        handleClose(id);
                    }}
                />
            ))}
        </DialogContext.Provider>
    );
};
